import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';

import { CoverageType, IBenefitsInformation } from '@sondermind/data-access/client-insurance';
import { IconColors, IconSizes, IrisIconComponent } from '@sondermind/iris/icon';

import { IInsuranceProgress } from '../../models/cost-estimation.model';

const COVERAGE_TYPE = CoverageType;

@Component({
  selector: 'client-insurance-progress-stepper',
  templateUrl: './insurance-progress-stepper.component.html',
  styleUrls: ['./insurance-progress-stepper.component.scss'],
  imports: [
    CommonModule,
    IrisIconComponent
  ]
})
export class InsuranceProgressStepperComponent implements OnInit {
  @Input() benefitsInfo!: IBenefitsInformation;

  IconColors = IconColors;
  IconSizes = IconSizes;

  planHasDeductible: boolean = false;
  planHasOutOfPocketMax: boolean = false;
  deductibleMet: boolean = false;
  outOfPockMaxMet: boolean = false;
  coPayAndCoinsuranceTypePlan: boolean = false;

  deductibleAmount: number = 0;
  coPayAmount: number = 0;
  coInsurancePercentage: number = 0;
  outOfPocketMaxAmount: number = 0;
  outOfPocketSessionCost: number = 0;

  steps: IInsuranceProgress[] = [];

  ngOnInit(): void {
    this.getPlanInfo();
    this.buildStepperInfo();
  }

  getPlanInfo(): void {
    switch (this.benefitsInfo?.benefitStructure?.name) {
      case COVERAGE_TYPE.COPAY_NO_DEDUCTIBLE_WITH_OOP_MAX:
        this.planHasOutOfPocketMax = true;
        this.coPayAmount = this.benefitsInfo.displayedClientCoPayAmount;
        this.outOfPocketMaxAmount = this.benefitsInfo.indOutOfPocketLimit.amount;
        this.outOfPocketSessionCost = 0;
        break;
      case COVERAGE_TYPE.COPAY_AFTER_DEDUCTIBLE_WITH_OOP_MAX:
        this.planHasDeductible = true;
        this.planHasOutOfPocketMax = true;
        this.deductibleAmount = this.benefitsInfo.indDeductibleLimit.amount;
        this.coPayAmount = this.benefitsInfo.displayedClientCoPayAmount;
        this.outOfPocketMaxAmount = this.benefitsInfo.indOutOfPocketLimit.amount;
        this.outOfPocketSessionCost = 0;
        break;
      case COVERAGE_TYPE.COPAY_AFTER_DEDUCTIBLE_NO_OOP_MAX:
        this.planHasDeductible = true;
        this.deductibleAmount = this.benefitsInfo.indDeductibleLimit.amount;
        this.coPayAmount = this.benefitsInfo.displayedClientCoPayAmount;
        break;
      case COVERAGE_TYPE.COINSURANCE_AFTER_DEDUCTIBLE_WITH_OOP_MAX:
        this.planHasDeductible = true;
        this.planHasOutOfPocketMax = true;
        this.deductibleAmount = this.benefitsInfo.indDeductibleLimit.amount;
        this.coInsurancePercentage = this.benefitsInfo.displayedClientCoInsurancePercent * 100;
        this.outOfPocketMaxAmount = this.benefitsInfo.indOutOfPocketLimit.amount;
        this.outOfPocketSessionCost = 0;
        break;
      case COVERAGE_TYPE.COINSURANCE_AFTER_DEDUCTIBLE_NO_OOP_MAX:
        this.planHasDeductible = true;
        this.deductibleAmount = this.benefitsInfo.indDeductibleLimit.amount;
        this.coInsurancePercentage = this.benefitsInfo.displayedClientCoInsurancePercent * 100;
        break;
      case COVERAGE_TYPE.COINSURANCE_NO_DEDUCTIBLE_WITH_OOP_MAX:
        this.planHasOutOfPocketMax = true;
        this.coInsurancePercentage = this.benefitsInfo.displayedClientCoInsurancePercent * 100;
        this.outOfPocketMaxAmount = this.benefitsInfo.indOutOfPocketLimit.amount;
        this.outOfPocketSessionCost = 0;
        break;
      case COVERAGE_TYPE.COPAY_AND_COINSURANCE_NO_DEDUCTIBLE_WITH_OOP_MAX:
        this.coPayAndCoinsuranceTypePlan = true;
        this.planHasOutOfPocketMax = true;
        this.coPayAmount = this.benefitsInfo.displayedClientCoPayAmount;
        this.coInsurancePercentage = this.benefitsInfo.displayedClientCoInsurancePercent * 100;
        this.outOfPocketMaxAmount = this.benefitsInfo.indOutOfPocketLimit.amount;
        this.outOfPocketSessionCost = 0;
        break;
      case CoverageType.COPAY_AND_COINSURANCE_AFTER_DEDUCTIBLE_WITH_OOP_MAX:
        this.coPayAndCoinsuranceTypePlan = true;
        this.planHasDeductible = true;
        this.planHasOutOfPocketMax = true;
        this.deductibleAmount = this.benefitsInfo.indDeductibleLimit.amount;
        this.coPayAmount = this.benefitsInfo.displayedClientCoPayAmount;
        this.coInsurancePercentage = this.benefitsInfo.displayedClientCoInsurancePercent * 100;
        this.outOfPocketMaxAmount = this.benefitsInfo.indOutOfPocketLimit.amount;
        this.outOfPocketSessionCost = 0;
        break;
      case CoverageType.COPAY_AND_COINSURANCE_AFTER_DEDUCTIBLE_NO_OOP_MAX:
        this.coPayAndCoinsuranceTypePlan = true;
        this.planHasDeductible = true;
        this.deductibleAmount = this.benefitsInfo.indDeductibleLimit.amount;
        this.coPayAmount = this.benefitsInfo.displayedClientCoPayAmount;
        this.coInsurancePercentage = this.benefitsInfo.displayedClientCoInsurancePercent * 100;
        break;
      case COVERAGE_TYPE.FULLY_COVERED_AFTER_DEDUCTIBLE:
        this.planHasDeductible = true;
        this.deductibleAmount = this.benefitsInfo.indDeductibleLimit.amount;
        break;
      default:
        break;
    }

    if (!this.planHasDeductible) {
      this.deductibleMet = false;
    } else {
      this.deductibleMet = this.benefitsInfo.indDeductibleRemaining.amount === 0;
    }

    if (!this.planHasOutOfPocketMax) {
      this.outOfPockMaxMet = false;
    } else {
      this.outOfPockMaxMet = this.benefitsInfo.indOutOfPocketRemaining.amount === 0;
    }
  }

  // Determines what steps are included in the the client's insurance plan
  // and what step they are currently on to determine what language/tense to use in the descriptions
  buildStepperInfo(): void {
    this.steps = [];

    if (this.planHasDeductible) {
      this.steps.push(
        {
          title: 'Start of plan year',
          description: this.deductibleMet ? 'You paid 100% of session fee' : 'You pay 100% of session fee',
          completed: true,
          inProgress: !this.deductibleMet && !this.outOfPockMaxMet
        },
        {
          title: this.deductibleMet ?
            `You met $${this.deductibleAmount} deductible` :
            `Meet $${this.deductibleAmount} deductible`,
          description: this.deductibleDescription(),
          completed: this.deductibleMet,
          inProgress: this.deductibleMet && !this.outOfPockMaxMet
        });
    }

    if (this.planHasOutOfPocketMax) {
      if (!this.planHasDeductible) {
        this.steps.push({
          title: 'Start of plan year',
          description: this.planStartDescription(),
          completed: true,
          inProgress: !this.outOfPockMaxMet
        });
      }

      this.steps.push(
        {
          title: this.outOfPockMaxMet ?
            `You met $${this.outOfPocketMaxAmount} out-of-pocket max` :
            `Meet $${this.outOfPocketMaxAmount} out-of-pocket max`,
          description: this.outOfPocketMaxDescription(),
          completed: this.outOfPockMaxMet,
          inProgress: this.outOfPockMaxMet
        });
    }
  }

  // Description of the client's first step of their insurance plan
  // only on if they have a copay and/or coinsurance type plan
  private planStartDescription(): string {
    if (this.coPayAndCoinsuranceTypePlan) {
      if (!this.planHasDeductible) {
        return this.outOfPockMaxMet ?
          `You paid ${this.coInsurancePercentage}% of session fee plus a $${this.coPayAmount} copay` :
          `You pay ${this.coInsurancePercentage}% of session fee plus a $${this.coPayAmount} copay`;
      }
    }

    if (this.coPayAmount) {
      return this.outOfPockMaxMet ?
        `You paid $${this.coPayAmount} copay per session` :
        `You pay $${this.coPayAmount} copay per session`;
    }

    if (this.coInsurancePercentage) {
      return this.outOfPockMaxMet ?
        `You paid ${this.coInsurancePercentage}% of session fee` :
        `You pay ${this.coInsurancePercentage}% of session fee`;
    }

    return '';
  }

  // Different tenses of the description of the dedecutible for copay and coinsurance
  private deductibleDescription(): string {
    if (this.coPayAndCoinsuranceTypePlan && this.planHasDeductible && this.planHasOutOfPocketMax) {
      if (!this.deductibleMet && !this.outOfPockMaxMet) {
        return `You will pay ${this.coInsurancePercentage}% of session fee, plus a $${this.coPayAmount} copay`;
      }

      if (this.deductibleMet && !this.outOfPockMaxMet) {
        return `You pay ${this.coInsurancePercentage}% of session fee, plus a $${this.coPayAmount} copay`;
      }

      if (this.deductibleMet && this.outOfPockMaxMet) {
        return `You paid ${this.coInsurancePercentage}% of session fee, plus a $${this.coPayAmount} copay`;
      }
    }

    if (this.coPayAndCoinsuranceTypePlan && this.planHasDeductible && !this.planHasOutOfPocketMax) {
      return this.deductibleMet ?
        `You pay ${this.coInsurancePercentage}% of session fee, plus a $${this.coPayAmount} copay` :
        `You will pay ${this.coInsurancePercentage}% of session fee, plus a $${this.coPayAmount} copay`;
    }

    if (this.benefitsInfo.displayedClientCoPayAmount) {
      if (!this.deductibleMet && !this.outOfPockMaxMet) {
        return `You  will pay a $${this.coPayAmount} copay per session`;
      }

      if (this.deductibleMet && !this.outOfPockMaxMet) {
        return `You pay a $${this.coPayAmount} copay per session`;
      }

      if (this.deductibleMet && this.outOfPockMaxMet) {
        return `You paid a $${this.coPayAmount} copay per session`;
      }
    } else if (this.benefitsInfo.displayedClientCoInsurancePercent) {
      if (!this.deductibleMet && !this.outOfPockMaxMet) {
        return `You  will pay ${this.coInsurancePercentage}% of session fee`;
      }

      if (this.deductibleMet && !this.outOfPockMaxMet) {
        return `You pay ${this.coInsurancePercentage}% of session fee`;
      }

      if (this.deductibleMet && this.outOfPockMaxMet) {
        return `You paid ${this.coInsurancePercentage}% of session fee`;
      }
    } else if (!this.benefitsInfo.displayedClientCoPayAmount && !this.benefitsInfo.displayedClientCoInsurancePercent) {
      if (!this.deductibleMet) {
        return 'You will pay $0 per session';
      }

      if (this.deductibleMet) {
        return 'You pay $0 per session';
      }
    }

    return '';
  }

  // Different tenses of OOP Max descriptions with support for coinsurance plans
  private outOfPocketMaxDescription(): string {
    // Out of pocket max has been met

    if (this.outOfPockMaxMet) {
      if (this.benefitsInfo.displayedClientCoInsurancePercent) {
        return `You pay 0% of session fee`;
      }

      return `You pay $${this.outOfPocketSessionCost} per session`;
    }

    // Out of pocket max has not been met

    if (this.benefitsInfo.displayedClientCoInsurancePercent) {
      return `You will pay 0% of session fee`;
    }

    return `You will pay $${this.outOfPocketSessionCost} per session`;
  }
}
